import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  MiBox,
  MiDetailFields,
  MiList,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { ICannotPerformReasonResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiFileDocumentOutline,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import { AddButton } from "@mibuildweb/components";
import { useCannotPerformReasons, usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { schema } from "./schemas/CannotPerformReason";
import { ConfigPageLayout } from "../../Config/ConfigPageLayout";
import { ConfigCard } from "../../Config/components";
import { ICannotPerformReasonData } from "./ManageCannotPerformReasons.types";

export function ManageCannotPerformReasons() {
  const [selectedCannotPerformReason, setSelectedCannotPerformReason] =
    useState<ICannotPerformReasonResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [refreshPagination, setRefreshPagination] = useState(false);

  const permissionEntity = PERMISSIONS.CANNOT_PERFORM_REASON;

  const defaultValues: ICannotPerformReasonData = {
    label: "",
  };

  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const typeLabel = "Cannot Perform Reason";

  const {
    formState: { isValid, isDirty },
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    isLoading,
    cannotPerformReasons,
    getCannotPerformReasons,
    setCannotPerformReasons,
    handleDeleteCannotPerformReason,
    refresh,
    setSearch,
    search,
    debouncedSearch,
    handleCreateCannotPerformReason,
    handleUpdateCannotPerformReason,
  } = useCannotPerformReasons();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleCloseAddModal = () => {
    setSelectedCannotPerformReason(null);
    setOpenAddModal(false);
    reset();
  };

  const handleOpenDeleteModal = (item: ICannotPerformReasonResponse) => {
    setSelectedCannotPerformReason(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = async () => {
    if (selectedCannotPerformReason) {
      await handleDeleteCannotPerformReason(selectedCannotPerformReason);
      setSelectedCannotPerformReason(null);
      setOpenDeleteModal(false);
    }
  };

  const handleAddCannotPerformReason = () => {
    setOpenAddModal(true);
    setSelectedCannotPerformReason(null);
    reset(defaultValues);
  };

  const handleSubmitCannotPerformReason = async () => {
    const data = getValues();
    if (selectedCannotPerformReason?.id) {
      await handleUpdateCannotPerformReason({
        ...data,
        id: selectedCannotPerformReason.id,
      });
    } else {
      await handleCreateCannotPerformReason(data);
    }
    handleCloseAddModal();
  };

  const cannotPerformReasonFields = [
    {
      label: "Label",
      name: "label",
      required: true,
      formControl: control,
      width: "100%",
    },
  ];

  const refreshData = useMemo(() => ({ refresh }), [refresh]);

  useEffect(() => {
    setSearch("");
    setRefreshPagination((prev) => !prev);
  }, []);

  return (
    <ConfigPageLayout
      title={`${typeLabel}s`}
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      hideOnBack
      loading={isLoading}
      titleActionComponent={
        hasPermission(permissionEntity?.CREATE) && (
          <AddButton
            label={`Add ${typeLabel}`}
            onClick={handleAddCannotPerformReason}
          />
        )
      }
    >
      <MiBox>
        <MiList
          viewType="card"
          data={cannotPerformReasons ?? []}
          dataProvider={getCannotPerformReasons}
          setData={setCannotPerformReasons}
          loading={isLoading}
          CardComponent={ConfigCannotPerformReasonsCard}
          cardProps={{
            permissionEntity,
            handleOpenDeleteModal,
            setOpenAddModal,
            setSelectedCannotPerformReason,
            reset,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          entityName={typeLabel}
          refresh={refreshData}
          refreshPagination={refreshPagination}
          emptyListProps={{
            text: `No ${typeLabel}s found`,
          }}
        />
      </MiBox>
      <MiModal
        open={openAddModal}
        titleIcon={
          selectedCannotPerformReason?.id
            ? mdiPencilOutline
            : mdiFileDocumentOutline
        }
        loading={isLoading}
        title={
          selectedCannotPerformReason?.id
            ? `Update ${typeLabel}`
            : `Create ${typeLabel}`
        }
        description={`Enter the label for ${typeLabel}`}
        closeModal={handleCloseAddModal}
        actions={[
          {
            name: BUTTON_LABELS.CANCEL,
            variant: BUTTON_VARIANTS.TEXT,
            onClick: handleCloseAddModal,
          },
          {
            name: `${selectedCannotPerformReason?.id ? "Update" : "Create"}`,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: handleSubmitCannotPerformReason,
            disabled: !isDirty || !isValid || isLoading,
          },
        ]}
      >
        <MiDetailFields detailFields={cannotPerformReasonFields} />
      </MiModal>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedCannotPerformReason?.label}. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.red.main,
        }}
      />
    </ConfigPageLayout>
  );
}

const ConfigCannotPerformReasonsCard = ({
  item,
  permissionEntity,
  handleOpenDeleteModal,
  setOpenAddModal,
  setSelectedCannotPerformReason,
  reset,
}: {
  item: ICannotPerformReasonResponse;
  permissionEntity:
    | typeof PERMISSIONS.RECORDABLE_TEMPLATE
    | typeof PERMISSIONS.SERVICE_ITEM_TEMPLATE;
  handleOpenDeleteModal: (item: ICannotPerformReasonResponse) => void;
  setOpenAddModal: (open: boolean) => void;
  setSelectedCannotPerformReason: (item: ICannotPerformReasonResponse) => void;
  reset: (data?: ICannotPerformReasonResponse) => void;
}) => {
  const { label, modifiedOn, createdOn } = item;
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const handleOpenEditModal = () => {
    setOpenAddModal(true);
    setSelectedCannotPerformReason(item);
    reset(item);
  };

  const actions = [
    {
      label: BUTTON_LABELS.EDIT,
      action: handleOpenEditModal,
      icon: mdiPencilOutline,
      permission: permissionEntity?.UPDATE,
    },
    {
      label: BUTTON_LABELS.DELETE,
      action: () => handleOpenDeleteModal(item),
      icon: mdiTrashCanOutline,
      color: theme.palette.red.main,
      permission: permissionEntity?.DELETE,
    },
  ].filter((action) => hasPermission(action.permission));

  return (
    <ConfigCard
      title={label}
      modifiedOn={modifiedOn}
      createdOn={createdOn}
      onClick={
        hasPermission(permissionEntity?.UPDATE)
          ? handleOpenEditModal
          : undefined
      }
      menuActions={actions}
    />
  );
};
